import type { InvalidateQueryFilters, QueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/queryKeys";

import type { Frames } from "../protocol/protocol";
import { invalidationsFor, type ContextDefs } from "./registry";

/** `workspace.changed`: a user-level frame, no rev; `workspace` is the id GET workspaces/ lists. */
export type WorkspaceChanged = Frames["workspace.changed"];

/** What the socket tells the data cache. The middleware calls these; it knows no query keys. */
export interface Invalidation {
  /** The user's assistant changed this context (`context.changed`). */
  contextChanged(ref: string): void;
  /** The socket came back: frames sent while it was down are lost, so refetch what is on screen. */
  reconnected(): void;
  /** `threads.changed`: the thread list order moved. */
  threadsChanged(): void;
  /** `workspace.changed`: a file was written to the workspace (or deleted); the Files panel refetches. */
  workspaceChanged(frame: WorkspaceChanged): void;
  /**
   * A call of `tool` ended (its `tool.result`, or its stored result). After a sandbox call the
   * workspaces and the Files panel refetch: new_sandbox makes a new one the current workspace, and
   * the others can write files. Covers what workspace.changed would, until the server sends it
   * (only declared so far: executor.new_workspace and file writes publish nothing).
   */
  toolEnded(tool: string): void;
}

// The sandbox tools that can change a workspace or its files: every tool in
// backend/apps/sandbox/tools except look, which only reads. new_sandbox opens a workspace, the
// rest can write files.
const WORKSPACE_TOOLS: ReadonlySet<string> = new Set([
  "new_sandbox",
  "bash",
  "repl",
  "write",
  "edit",
  "apply_patch",
  "load_data",
]);

/**
 * Several tool calls in a row send one frame each: refetch once per ref (or workspace) after a
 * short quiet time.
 */
export const CONTEXT_DEBOUNCE_MS = 250;

interface Timers {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

export function createInvalidation(
  queryClient: Pick<QueryClient, "invalidateQueries" | "cancelQueries">,
  defs: ContextDefs,
  threadsKey: readonly unknown[],
  timers: Timers = {
    setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
    clearTimeout: (handle) => globalThis.clearTimeout(handle as number | undefined),
  }
): Invalidation {
  const waiting = new Map<string, unknown>();
  const invalidateAll = (filters: InvalidateQueryFilters[]) =>
    filters.forEach((filter) => void queryClient.invalidateQueries(filter));
  const invalidateSoon = (key: string, filters: InvalidateQueryFilters[]) => {
    timers.clearTimeout(waiting.get(key));
    waiting.set(
      key,
      timers.setTimeout(() => {
        waiting.delete(key);
        invalidateAll(filters);
      }, CONTEXT_DEBOUNCE_MS)
    );
  };

  return {
    contextChanged(ref) {
      const filters = invalidationsFor(defs, ref);
      if (filters.length) invalidateSoon(ref, filters);
    },
    reconnected() {
      void queryClient.invalidateQueries({ refetchType: "active" });
    },
    threadsChanged() {
      void queryClient.invalidateQueries({ queryKey: threadsKey });
    },
    workspaceChanged({ workspace }) {
      // The listing, and every thread's workspace entry (its usage feeds the composer's warning).
      invalidateSoon(`workspace:${workspace}`, [
        { queryKey: queryKeys.agentWorkspaceFiles(workspace) },
        { queryKey: queryKeys.agentWorkspacesAll() },
      ]);
    },
    toolEnded(tool) {
      if (!WORKSPACE_TOOLS.has(tool)) return;
      // Every thread's list, not only the caller's: a bound thread's workspaces are its context's,
      // shared by the context's other threads (the server's place_of).
      const workspaces = { queryKey: queryKeys.agentWorkspacesAll() };
      // Marked at once, so an upload in the quiet time asks for the list (useThreadWorkspace). A
      // fetch already on its way may carry the list from before the call, and its answer would
      // clear the mark: it is cancelled first (rolled back), and the refetch below asks again.
      void queryClient.cancelQueries(workspaces);
      void queryClient.invalidateQueries({ ...workspaces, refetchType: "none" });
      // The lists (the current workspace), and the listing on screen (its files).
      invalidateSoon("workspaces", [workspaces, { queryKey: queryKeys.agentWorkspaceFilesAll() }]);
    },
  };
}
